import React from "react";
import { Plus, Trash2, GripVertical } from "lucide-react";

import { Input, Select, Button, IconButton, Tooltip } from "../../design-system";
import { PROJECT_LINK_TYPES, getProjectLinkType } from "../../utils/projectLinks";

/**
 * Répéteur de liens d'un projet : type (icône), libellé, URL.
 *
 * Le libellé est facultatif — le public retombe sur le libellé du type
 * (« GitHub », « Démo »…), ce qui suffit dans la majorité des cas.
 */
export default function ProjectLinksInput({ value = [], onChange }) {
  const links = Array.isArray(value) ? value : [];

  const update = (index, field, fieldValue) =>
    onChange(links.map((link, i) => (i === index ? { ...link, [field]: fieldValue } : link)));

  const move = (index, delta) => {
    const target = index + delta;
    if (target < 0 || target >= links.length) return;
    const next = [...links];
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  };

  return (
    <div className="space-y-3">
      {links.length === 0 ? (
        <div className="rounded-md border border-dashed border-border px-4 py-6 text-center">
          <p className="text-sm text-mute italic">
            Aucun lien. Dépôt, démo en ligne, documentation…
          </p>
        </div>
      ) : (
        <ul className="space-y-2">
          {links.map((link, index) => {
            const Icon = getProjectLinkType(link.type).icon;
            return (
              <li
                key={index}
                className="flex items-start gap-2 p-2.5 rounded-md bg-bg-2/40 border border-border"
              >
                <div className="flex flex-col pt-1.5">
                  <button
                    type="button"
                    onClick={() => move(index, -1)}
                    disabled={index === 0}
                    aria-label="Monter ce lien"
                    className="text-mute hover:text-text disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <GripVertical className="w-4 h-4" strokeWidth={1.8} />
                  </button>
                </div>

                <div className="flex items-center justify-center w-9 h-10 shrink-0 text-text">
                  <Icon className="w-4 h-4" strokeWidth={1.8} />
                </div>

                <div className="flex-1 min-w-0 grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <Select
                    value={link.type || "other"}
                    onChange={(e) => update(index, "type", e.target.value)}
                    aria-label={`Type du lien ${index + 1}`}
                  >
                    {PROJECT_LINK_TYPES.map((option) => (
                      <option key={option.type} value={option.type}>{option.label}</option>
                    ))}
                  </Select>
                  <Input
                    value={link.label || ""}
                    onChange={(e) => update(index, "label", e.target.value)}
                    placeholder={getProjectLinkType(link.type).label}
                    aria-label={`Libellé du lien ${index + 1}`}
                  />
                  <Input
                    type="url"
                    value={link.url || ""}
                    onChange={(e) => update(index, "url", e.target.value)}
                    placeholder="https://…"
                    aria-label={`URL du lien ${index + 1}`}
                  />
                </div>

                <Tooltip content="Supprimer ce lien">
                  <IconButton
                    variant="ghost"
                    icon={<Trash2 strokeWidth={1.8} />}
                    aria-label={`Supprimer le lien ${index + 1}`}
                    onClick={() => onChange(links.filter((_, i) => i !== index))}
                  />
                </Tooltip>
              </li>
            );
          })}
        </ul>
      )}

      <Button
        type="button"
        size="sm"
        variant="secondary"
        leftIcon={<Plus className="w-4 h-4" strokeWidth={1.8} />}
        onClick={() => onChange([...links, { type: "demo", label: "", url: "" }])}
      >
        Ajouter un lien
      </Button>
    </div>
  );
}
